import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ImapServer } from '../types/models/ImapServer';
import type { ApiResponseImapServerList } from '../types/api/ApiResponseImapServer';

interface UseImapServersReturn {
  imapServers: ImapServer[];
  loading: boolean;
  error: string | null;
}

// `enabled` (default true) defers the fetch: a consumer that only needs the servers conditionally —
// e.g. a modal passing `isOpen` — avoids hitting /emails/imap-servers until it actually opens, and
// refetches each time it re-enables.
export const useImapServers = (enabled = true): UseImapServersReturn => {
  const [imapServers, setImapServers] = useState<ImapServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchImapServers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/emails/imap-servers');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseImapServerList;
      if (data.status === 'success' && data.data?.imap_servers) {
        setImapServers(data.data.imap_servers);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to load IMAP servers');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching IMAP servers');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated && enabled) {
      fetchImapServers();
    }
  }, [auth.isAuthenticated, enabled, fetchImapServers]);

  return { imapServers, loading, error };
};
